import { Component, Input, Output, EventEmitter, ViewChild, OnInit, OnDestroy } from '@angular/core';
import { NgForm } from '@angular/forms';
import { ApprovalStatus, ApprovalType } from 'src/app/modules/shared/enums/approval/approval.enum';
import { ApprovalInfoDetails, ApprovalAttachment, ApprovalActivityInfo, ApprovalTypeInfo } from 'src/app/modules/shared/model/approval/approval-info';
import { saveAs } from "file-saver";
import { AppMainComponent } from 'src/app/app.main.component';
import { AppMainService } from 'src/app/app-main.service';
import { MessageService } from 'primeng/api';
import { MyDocumentsService } from '../../my-documents/my-documents.service';
import { DocumentViewerService } from 'src/app/modules/shared/services/document-viewer.service';
import { Status } from 'src/app/modules/shared/models/status';
import { uploadFiles, validateFiles } from 'src/app/modules/shared/upload-files';
import { TransactionNames } from '../../../finance/constants/constants';
import { ApprovalService } from 'src/app/modules/shared/services/approval/approval.http.service';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'app-approval-details',
  templateUrl: './approval-details.component.html',
  styleUrls: ['./approval-details.component.scss']
})
export class ApprovalDetailsComponent implements OnInit, OnDestroy {
  @Input() approvalActivityInfo?:ApprovalActivityInfo
  @Input() displayViewApproval: boolean;
  @Input() approvalTypeInfo?: ApprovalTypeInfo;
  @Input() encryptedKey?: number;
  @Output() approvalStatusChange? = new EventEmitter<{isUpdate:boolean }>();
  @Output() cancelClick = new EventEmitter<void>();

  @ViewChild('approvalForm') approvalForm: NgForm;

  protected ngUnsubscribe$: Subject<void> = new Subject<void>();
  
  approvalDetails: ApprovalInfoDetails;
  /**Form */
  approvalDescription: string = '';

  /**Approval enum */
  ApprovalStatus = ApprovalStatus;
  ApprovalType = ApprovalType;

  /**show more */
  isExpanded: any = []

    /**Variables related to view the invoice  */
  displayInventoryOverview: boolean = false;
  inventoryOverviewRefId: number = 0;
  inventoryOverviewRefNumber: string = "";
  inventoryOverviewModule: string = "";
  transactionNames = TransactionNames;

  /** status configuration objects */
  statusStyles = {
    [ApprovalStatus.Approved]: {
      backgroundColor: 'green',
      color: 'white',
      iconClass: 'pi pi-check-circle',
      borderColor: 'darkgreen'
    },
    [ApprovalStatus.Declined]: {
      backgroundColor: 'red',
      color: 'white',
      iconClass: 'pi pi-times-circle',
      borderColor: 'darkred'
    },
    [ApprovalStatus.Pending]: {
      backgroundColor: 'orange',
      color: 'white',
      iconClass: 'pi pi-clock',
      borderColor: 'darkorange'
    },
    [ApprovalStatus.Queued]: {
      backgroundColor: 'gray',
      color: 'white',
      iconClass: 'pi pi-clock',
      borderColor: 'darkgray'
    },
    [ApprovalStatus.PartiallyApproved]: {
      backgroundColor: 'blue',
      color: 'white',
      iconClass: 'pi pi-info-circle',
      borderColor: 'darkblue'
    },
    [ApprovalStatus.Default]: {
      backgroundColor: 'deeppink',
      color: 'white',
      iconClass: 'pi pi-info',
      borderColor: 'deeppink',
    },
  };
  /** Attachments */
  attachments: ApprovalAttachment[] = [];
  mimeTypes = [
    { extension: "csv", value: "text/csv" },
    { extension: "doc", value: "application/msword" },
    { extension: "docx", value: "application/msword" },
    { extension: "gif", value: "image/gif" },
    { extension: "jpeg", value: "image/jpeg" },
    { extension: "jpg", value: "image/jpeg" },
    { extension: "png", value: "image/png" },
    { extension: "pdf", value: "application/pdf" },
    { extension: "ppt", value: "application/vnd.ms-powerpoint" },
    { extension: "pptx", value: "application/vnd.ms-powerpoint" },
    { extension: "rar", value: "application/x-rar-compressed" },
    { extension: "xls", value: "application/msexcel" },
    { extension: "xlsx", value: "application/msexcel" },
    { extension: "zip", value: "application/zip" },
    { extension: "txt", value: "text/plain" }
  ];
  allowedExtensions = ['.pdf', '.csv', '.txt', '.xlsx', '.xls', '.docx', '.doc', '.png', '.jpg', '.jpeg', '.ppt', '.pptx', '.gif', '.zip']; // For the attachment files
  multiLineTooltip = `<ul class="p-mt-0 p-pl-4 p-mb-0">
    <li>Files with extensions: doc, docx, pdf, xls, xlsx, txt, gif, jpeg are allowed</li>
</ul> `;

 /**Flag to track submission state */
 isSubmitting: boolean = false;

  constructor(
    public app: AppMainComponent,
    private appMainService: AppMainService,
    private messageService: MessageService,
    private documentsService: MyDocumentsService,
    private documentViewerService: DocumentViewerService,
    private approvalService: ApprovalService,
  ) { }

  ngOnInit(): void {
    if (this.approvalTypeInfo) {
      this.getApprovalDetailsByType(this.approvalTypeInfo);
    }
    else if(this.approvalActivityInfo){
      this.getApprovalDetailsById(this.approvalActivityInfo)
    }
    else if(this.encryptedKey){
      this.getApprovalDetailsByKey(this.encryptedKey)
    }
    }

  getStatusStyles(status: ApprovalStatus) {
    return this.statusStyles[status] || {};
  }

  toggleDescription(index) {
    this.isExpanded[index] = !this.isExpanded[index];
  }

  getApprovalDetailsByType(approvalTypeInfo: ApprovalTypeInfo) {
    const { approvalType, approvalTypeId, approvalTypeNumber, status } = approvalTypeInfo;
    this.appMainService.isShowSpinner('getApprovalDetailsByType');
    this.approvalService.getApprovalDetailsByType(approvalType, approvalTypeId, status, approvalTypeNumber).pipe(takeUntil(this.ngUnsubscribe$)).subscribe({
      next: (response: ApprovalInfoDetails) => {
        this.appMainService.isHideSpinner('getApprovalDetailsByType');
        this.approvalDetails = response;
      },
      error: (error) => {
        this.appMainService.isHideSpinner('getApprovalDetailsByType');
      }
    })
  }

  getApprovalDetailsByKey(encryptedKey:number) {
    this.appMainService.isShowSpinner('getApprovalDetailsByKey');
    this.approvalService.getApprovalDetailsByKey(encryptedKey).pipe(takeUntil(this.ngUnsubscribe$)).subscribe({
      next: (response: ApprovalInfoDetails) => {
        this.appMainService.isHideSpinner('getApprovalDetailsByKey');
        this.approvalDetails = response;
      },
      error: (error) => {
        this.appMainService.isHideSpinner('getApprovalDetailsByKey');
      }
    })
  }

  getApprovalDetailsById(approvalActivityInfo: ApprovalActivityInfo) {
    const { approvalId, approvalInfoId, statusId } = approvalActivityInfo;
    this.appMainService.isShowSpinner('getApprovalDetailsById');
    this.approvalService.getApprovalDetailsById(approvalId, approvalInfoId, statusId).pipe(takeUntil(this.ngUnsubscribe$)).subscribe({
      next: (response: ApprovalInfoDetails) => {
        this.appMainService.isHideSpinner('getApprovalDetailsById');
        this.approvalDetails = response;
      },
      error: (error) => {
        this.appMainService.isHideSpinner('getApprovalDetailsById');
      }
    })
  }

  handleApprovalStatus(approvalStatus: ApprovalStatus, form: any) {
    if (this.isSubmitting) {
      return; 
    }
    const approvalDetails:ApprovalInfoDetails =this.approvalDetails;
    const approvalAttachments: ApprovalAttachment[] = [...this.attachments];
    const description = form.value.description
    form.control.markAllAsTouched();
    if (form.invalid || !approvalDetails) {
      return;
    }  
    this.isSubmitting = true; 
    const submitApprovalDetails: any = {
      approvalId: approvalDetails.id,
      receiverId: approvalDetails.approvalInfo.receiverId,
      id: approvalDetails.approvalInfo.id,
      approvalStatus: approvalStatus,
      description: description,
      approvalSequence: approvalDetails.approvalInfo.approvalSequence,
      statusId: approvalDetails.statusId,
      ReceiverName: approvalDetails.approvalInfo.receiverName,
      approvalAttachments: approvalAttachments || null
    }
    const queryParams: any = {
      approvalInfoId: approvalDetails?.approvalInfo?.id
    };
    this.appMainService.isShowSpinner('updateApproval');
    this.approvalService.updateApproval(approvalDetails.id, submitApprovalDetails, queryParams).pipe(takeUntil(this.ngUnsubscribe$)).subscribe({
      next: (response) => {
        this.appMainService.isHideSpinner('updateApproval');
        this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Approved successfully.' });
        this.displayViewApproval = false;
        const isUpdate=true;
        this.approvalStatusChange.emit({isUpdate});
      },
      error: (error) => {
        this.appMainService.isHideSpinner('updateApproval');
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Try after sometime.' });
      },
      complete: () => {
        this.isSubmitting = false;
      }
    })    
  }

  resetForm() {
    this.approvalForm.resetForm();
    this.attachments = []

  }

  onClose() {
    this.resetForm();
    this.cancelClick.emit();
  }

  uploadAttachments(files: File[]) {
    this.appMainService.isShowSpinner('upload');
    if (typeof Worker !== undefined) {
      for (let file of files) {
        let errors = validateFiles(file, 2, this.allowedExtensions);
        if (errors.length > 0) {
          for (let error of errors) {
            this.messageService.add({ severity: 'error', summary: 'Error', detail: error.fileName + ": " + error.error });
          }
          this.appMainService.isHideSpinner('upload');
        } else {
          uploadFiles(file, ({ name, content, size, extension }) => {

            let mime_type = this.mimeTypes.find(el => el.extension === extension.toLowerCase());

            let attachment: any = {
              id: 0,
              createdSource: this.app.userIP,
              updatedSource: this.app.userIP,
              createdBy: this.app.user.userId,
              updatedBy: 0,
              createdDate: new Date(),
              updatedDate: new Date(),
              name: name,
              guidName: "",
              content: content,
              extension: extension,
              sizeInBytes: size,
              mediaType: mime_type?.value || "",
              description: "",
              statusId: Status.Active,
              isValid: true,
              moduleName: 'Approvals',
              isModuleFolder: true
            };

            this.attachments.push({
              approvalInfoId: this.approvalDetails.approvalInfo.id,
              id: 0,
              fileName: name,
              displayFileName: name,
              filePath: name,
              mediaType: mime_type?.value || "",
              fileSize: size,
              statusId: Status.Active,
              attachment: attachment
            });
            this.appMainService.isHideSpinner('upload');
          });
        }
      }
    } else {
      this.messageService.add({ severity: 'error', summary: 'Error', detail: "Please update the browser. Web Workers are not supported in the older versions of this browser." });
    }
  }

  downloadFile(name: string, content: string, attachmentId: number) {
    let ext = name.substring(name.lastIndexOf('.') + 1);
    ext = ext?.toLowerCase();
    let mime_type = this.mimeTypes.filter(el => el.extension === ext);
    if (mime_type.length > 0 && content) {
      const base64 = content;
      const mime = mime_type[0].value;
      const blob = this.dataURItoBlob(base64, mime);
      const file = new File([blob], name, { type: mime });
      saveAs(file, name)
    } else if (attachmentId > 0) {
      this.appMainService.isShowSpinner('downloadLink');
      this.documentsService.downloadPublicFile(attachmentId, true).subscribe({
        next: (data) => {
          this.downloadedFile(data?.downloadLink);
          this.appMainService.isHideSpinner('downloadLink');
        },
        error: (error: any) => {
          this.appMainService.isHideSpinner('downloadLink');
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: "Error occurred while download this file...",
          });
        }
      })
    } else {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: "No content found to download...",
      });
    }
  }
  downloadedFile(url: string) {
    const a = document.createElement('a');
    a.style.display = 'none';
    a.href = url;
    // the filename you want
    a.download = 'todo-1.json';
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
  }

  dataURItoBlob(dataURI: string, mimeType: string) {
    const kept = dataURI.split(",", 2);
    const byteString = atob(kept[1]);
    const arrayBuffer = new ArrayBuffer(byteString.length);
    const int8Array = new Uint8Array(arrayBuffer);
    for (let i = 0; i < byteString.length; i++) {
      int8Array[i] = byteString.charCodeAt(i);
    }
    const blob = new Blob([int8Array], { type: mimeType });
    return blob;
  }

  openFileNewTab(attachmentDetails: any) {
    if (attachmentDetails?.id > 0) {

      this.appMainService.isShowSpinner('openFilePreview');
      let docType = attachmentDetails.name.substring(attachmentDetails.name.lastIndexOf('.') + 1);
      docType = docType?.toLowerCase();

      this.documentsService.downloadPublicFile(attachmentDetails.id, false).subscribe({
        next: (data) => {
          switch (docType) {
            case 'docx':
              this.documentViewerService.selectDocumentType(docType, data?.encodedDownloadLink);
              break;
            case 'xlsx':
              this.documentViewerService.selectDocumentType(docType, data?.encodedDownloadLink);
              break;
            case 'pptx':
              this.documentViewerService.selectDocumentType(docType, data?.encodedDownloadLink);
              break;
            case 'csv':
              this.documentViewerService.selectDocumentType(docType, data?.encodedDownloadLink);
              break;
            case 'pdf':
              this.documentViewerService.selectDocumentType(docType, data?.downloadLink);
              break;
            case 'jpg':
              this.documentViewerService.selectDocumentType(docType, data?.downloadLink);
              break;
            case 'png':
              this.documentViewerService.selectDocumentType(docType, data?.downloadLink);
              break;
            case 'gif':
              this.documentViewerService.selectDocumentType(docType, data?.downloadLink);
              break;
            case 'txt':
              this.documentViewerService.selectDocumentType(docType, data?.downloadLink);
              break;
            default:
              this.documentViewerService.selectDocumentType(docType, data?.downloadLink);
          }
          this.appMainService.isHideSpinner('openFilePreview');
        },
        error: (error: any) => {
          this.appMainService.isHideSpinner('openFilePreview');
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: "Error occurred while open this file...",
          });
        }
      })
    }
  }

  removeAttachment(attachment: any, index: number) {
    if (attachment?.id > 0) {
      attachment.statusId = Status.Deleted;
    } else {
      this.attachments.splice(index, 1);
    }
  }

  closeViewPopup() {
    this.displayInventoryOverview = false;
  }
  openInventoryOverview(moduleName: string, id: number, title: string) {
    this.inventoryOverviewRefId = id;
    this.inventoryOverviewRefNumber = title;
    this.inventoryOverviewModule = moduleName;
    this.displayInventoryOverview = true;
  }

  navigateToTransactionTypeDetailsPage(id: ApprovalType, title: string) {
    switch (id) {
      case ApprovalType.EstimateApproval:
        this.openInventoryOverview(this.transactionNames.Estimate, 0, title);
        break;
      case ApprovalType.SalesOrderApproval:
        this.openInventoryOverview(this.transactionNames.SalesOrder, 0, title);
        break;
      case ApprovalType.InvoiceApproval:
        this.openInventoryOverview(this.transactionNames.Invoice, 0, title);
        break;
      default:
        break;
    }
  }

  getApprovalTypeLabel(type: ApprovalType) {
    switch (type) {
      case ApprovalType.EstimateApproval:
        return 'Estimate No.';
      case ApprovalType.SalesOrderApproval:
        return 'Sales Order No.';
      case ApprovalType.InvoiceApproval:
        return 'Invoice No.';
      default:
        break;
    }
  }

  ngOnDestroy(): void {
    if (this.ngUnsubscribe$) {
      this.ngUnsubscribe$.next();
      this.ngUnsubscribe$.complete();
      this.ngUnsubscribe$.unsubscribe();
    }
  }
}